/**
 * ChatRowDeleteConfirm — inline-confirmation affordance for deleting a
 * chat thread from a conversations-list row.
 *
 * Replaces the previous "open a modal" pattern with an in-row morph:
 * the conversations row stays in the same vertical slot, the title is
 * cross-faded for a "Delete this chat?" prompt, and Cancel / Delete
 * buttons slide in from the right (translate-x-2 → translate-x-0,
 * opacity-0 → opacity-100, 200ms ease-out). Tailwind's ``transition``
 * utilities cover the motion — no Framer Motion dependency. Reduced-
 * motion users get the instant swap via the ``motion-safe:`` gate on
 * the transitions.
 *
 * DRY across surfaces (Leo, 2026-04-30): this component is intended to
 * mount inside both
 *   - ``components/portal/TrackerSidebar`` (the always-mounted left
 *     rail at ``/portal/chat``), and
 *   - ``components/chat/ChatSidebar`` (the global Sheet surface used
 *     elsewhere in the portal),
 * so both surfaces fire the same DELETE call through the same
 * ``useDeleteChatThreadMutation`` and get the same row UX. Forking
 * the row into two surface-specific components would be the kind of
 * cross-surface drift Leo's DRY rule explicitly guards against.
 *
 * Keyboard model (a11y):
 *   - Escape collapses confirm → default (calls ``onCancel``).
 *   - Enter while focused inside the row fires Delete (calls
 *     ``onConfirm``).
 * Both keys are scoped to events that bubble out of this component;
 * we don't attach a ``window`` listener so other dialog primitives
 * keep their Esc semantics.
 *
 * Errors (PRD: errors are first-class):
 *   - When ``error`` is non-null we keep the row in confirm state and
 *     render the error message *under* the buttons. The Delete button
 *     stays clickable so the user can retry without re-entering
 *     confirm. The Cancel button still backs out cleanly.
 */
import React, { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

export interface ChatRowDeleteConfirmProps {
  /**
   * Visible text on the confirm prompt. Defaults to the standard
   * "Delete this chat?" but callers can override (e.g. for
   * not-yet-localized internal admin surfaces).
   */
  prompt?: string;
  /**
   * Fires when the user presses Cancel, or hits Escape while focus
   * is inside the row. Caller is responsible for collapsing back to
   * default state.
   */
  onCancel: () => void;
  /**
   * Fires when the user presses Delete or hits Enter. Caller wires
   * this to the mutation. The component does NOT call the mutation
   * directly so the parent can sequence side-effects (e.g. clearing
   * the active thread when the deleted one is currently open).
   */
  onConfirm: () => void;
  /**
   * In-flight indicator — disables both buttons while true so the
   * user can't double-click. The mutation hook owns the actual
   * fetch; this prop just mirrors its ``isPending``.
   */
  isPending?: boolean;
  /**
   * Inline error message rendered under the buttons. Pass ``null``
   * when there's no active error. Keeping the row in confirm mode
   * on error is the parent's responsibility.
   */
  error?: string | null;
  /**
   * Identifier surfaced as ``data-thread-id`` on the root container
   * so tests can target a specific row's confirm state.
   */
  threadId: string;
}

const ChatRowDeleteConfirm: React.FC<ChatRowDeleteConfirmProps> = ({
  prompt = "Delete this chat?",
  onCancel,
  onConfirm,
  isPending = false,
  error = null,
  threadId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const deleteButtonRef = useRef<HTMLButtonElement | null>(null);

  // On mount, focus the destructive Delete button. Rationale:
  //   - The user just clicked the kebab-menu Delete item, so their
  //     intent is destructive; auto-focusing Delete shaves one Tab.
  //   - Enter immediately fires the destructive action — see the
  //     keydown handler below — which is the right shortcut for the
  //     "I really meant to delete that" path.
  //   - Cancel is one Tab away (or Escape), which is the standard
  //     "back out" path.
  useEffect(() => {
    deleteButtonRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (!isPending) onConfirm();
    }
  };

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label="Confirm delete chat"
      data-testid="chat-row-delete-confirm"
      data-thread-id={threadId}
      onKeyDown={handleKeyDown}
      className={cn(
        // Match the row footprint of the default state — the title
        // row uses px-3 py-2 in TrackerSidebar; we match that so the
        // morph doesn't shift the row's vertical slot in the list.
        "flex items-center justify-between gap-2 px-3 py-2 rounded-md",
        "bg-paper-panel motion-safe:transition-colors motion-safe:duration-200",
      )}
    >
      <span
        className={cn(
          "text-2xs text-ink truncate min-w-0",
          // Slide-in for the prompt — same easing curve as the buttons
          // so the whole morph reads as one motion.
          "motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out",
          "translate-x-0 opacity-100",
        )}
        data-testid="chat-row-delete-confirm-prompt"
      >
        {prompt}
      </span>
      <div
        className={cn(
          "flex items-center gap-1.5 shrink-0",
          "motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out",
          // Mounted state — buttons sit at translate-x-0 / opacity-100.
          // The starting state (translate-x-2 / opacity-0) is applied
          // via the Tailwind ``data-[state=enter]:`` selector below if
          // the parent ever wants to re-trigger the animation; on
          // first mount React renders us at this final state which is
          // already the "after" snapshot. Browsers don't animate
          // first-paint, so the perceived motion comes from the
          // sibling row's title fading out (parent owns that).
          "translate-x-0 opacity-100",
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          disabled={isPending}
          data-testid="chat-row-delete-cancel"
          aria-label="Cancel delete"
          className={cn(
            "px-2 py-1 text-3xs font-medium text-ink-mute",
            "hover:text-ink hover:bg-ink-line rounded transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink-line-strong",
          )}
        >
          Cancel
        </button>
        <button
          ref={deleteButtonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onConfirm();
          }}
          disabled={isPending}
          data-testid="chat-row-delete-confirm-button"
          aria-label="Confirm delete chat"
          aria-busy={isPending}
          className={cn(
            "px-2 py-1 text-3xs font-medium text-destructive-foreground bg-destructive",
            "hover:bg-destructive/90 rounded transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-destructive",
          )}
        >
          {isPending ? "Deleting…" : "Delete"}
        </button>
      </div>
      {error ? (
        // Error copy lands on a second line, full-row width, to keep
        // the buttons in their original slot. The mutation's
        // ``isError`` keeps this row in confirm mode automatically;
        // the user can retry by clicking Delete again or back out
        // with Cancel.
        <p
          role="alert"
          data-testid="chat-row-delete-error"
          className="basis-full mt-1 text-3xs text-destructive leading-snug"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default ChatRowDeleteConfirm;
